import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useGetRequest } from '@/shared/hooks/requests';
import { FILE_ACTIVITY } from '@/shared/utils/urls';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  FileText,
  Edit,
  Share2,
  Upload,
  History,
  PenTool,
  MessageSquare,
  Eye,
  Trash2,
  Copy,
  Loader2,
  User,
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

/**
 * ActivityPanel - Shows activity timeline for a document
 */
export function ActivityPanel({ open, onOpenChange, fileId }) {
  const { t } = useTranslation(['my-docs', 'common']);
  const [activities, setActivities] = useState([]);

  const getActivity = useGetRequest({
    url: FILE_ACTIVITY.replace('{id}', fileId),
  });

  useEffect(() => {
    if (open && fileId) {
      loadActivity();
    }
  }, [open, fileId]);

  const loadActivity = async () => {
    const { response, success } = await getActivity.request();
    if (success && response?.activities) {
      setActivities(response.activities);
    }
  };

  // Get icon for action type
  const getActionIcon = (action) => {
    const iconMap = {
      'file.created': FileText,
      'file.viewed': Eye,
      'file.edited': Edit,
      'file.deleted': Trash2,
      'file.restored': History,
      'file.cloned': Copy,
      'file.visibility_changed': Eye,
      'version.uploaded': Upload,
      'version.restored': History,
      'share.created': Share2,
      'share.revoked': Share2,
      'share.link_generated': Share2,
      'signing.requested': PenTool,
      'signing.signed': PenTool,
      'signing.rejected': PenTool,
      'signing.completed': PenTool,
      'comment.added': MessageSquare,
      'comment.resolved': MessageSquare,
      'comment.deleted': MessageSquare,
    };
    const Icon = iconMap[action] || Clock;
    return Icon;
  };

  // Get color for action type
  const getActionColor = (action) => {
    if (action.startsWith('file.deleted')) return 'text-red-500 bg-red-50';
    if (action.startsWith('signing')) return 'text-purple-500 bg-purple-50';
    if (action.startsWith('share')) return 'text-blue-500 bg-blue-50';
    if (action.startsWith('version')) return 'text-orange-500 bg-orange-50';
    if (action.startsWith('comment')) return 'text-green-500 bg-green-50';
    if (action.includes('edited')) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-500 bg-gray-50';
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return t('time.justNow');
    if (diffMin < 60) return t('time.minutesAgo', { count: diffMin });
    if (diffHour < 24) return t('time.hoursAgo', { count: diffHour });
    if (diffDay < 7) return t('time.daysAgo', { count: diffDay });

    return date.toLocaleDateString('ru-RU');
  };

  // Get user initials
  const getUserInitials = (fullName) => {
    if (!fullName) return '?';
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t('activity.title')}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6">
          {getActivity.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">{t('common:loading')}</span>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('activity.noActivity')}</p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100dvh-150px)]">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />

                {/* Activity items */}
                <div className="space-y-4">
                  {activities.map((activity, index) => {
                    const Icon = getActionIcon(activity.action);
                    const colorClass = getActionColor(activity.action);

                    return (
                      <div key={activity.id || index} className="relative flex gap-4 pl-2">
                        {/* Icon */}
                        <div
                          className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${colorClass}`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 pb-4">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-sm text-gray-900">
                                {activity.action_label || activity.action}
                              </p>
                              {activity.details && (
                                <p className="text-sm text-gray-500">{activity.details}</p>
                              )}
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {formatRelativeTime(activity.timestamp)}
                            </span>
                          </div>

                          {/* Actor */}
                          {activity.actor && (
                            <div className="flex items-center gap-2 mt-2">
                              <Avatar className="w-5 h-5">
                                <AvatarFallback className="text-xs bg-gray-100">
                                  {getUserInitials(activity.actor.full_name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-xs text-gray-500">
                                {activity.actor.full_name || activity.actor.email}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
